import { useState, useEffect, useRef, useCallback } from "react";
import { invoke } from "../lib/bridge";
import { QRCodeSVG } from "qrcode.react";
import { buildSnapshots } from "../lib/snapshots";
import { Spinner } from "./ui/Spinner";

interface InviteData {
  payload: string;
  code: string;
  expiresAt: string;
}

interface InviteViewProps {
  onBack: () => void;
}

export async function generateAndPushSnapshots(inviteCode: string) {
  try {
    const snapshots = await buildSnapshots();
    if (snapshots.length > 0) {
      await invoke("push_snapshots", { inviteCode, snapshots });
    }
  } catch (e) {
    console.warn("Snapshot push failed (non-fatal):", e);
  }
}

export function InviteView({ onBack }: InviteViewProps) {
  const [invite, setInvite] = useState<InviteData | null>(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval>>(undefined);

  const fetchCode = useCallback(() => {
    setError("");
    invoke<InviteData>("get_invite_code")
      .then((data) => {
        setInvite(data);
        // Push snapshots in the background — non-blocking
        generateAndPushSnapshots(data.code);
      })
      .catch((e) => {
        console.error("get_invite_code failed:", e);
        setError(String(e));
      });
  }, []);

  useEffect(() => {
    fetchCode();
  }, [fetchCode]);

  useEffect(() => {
    if (!invite) return;

    const tick = () => {
      const expiresAtMs = new Date(invite.expiresAt).getTime();
      const secs = Math.max(0, Math.floor((expiresAtMs - Date.now()) / 1000));
      setRemaining(secs);
      if (secs <= 0) fetchCode();
    };

    tick();
    timerRef.current = setInterval(tick, 1000);
    return () => clearInterval(timerRef.current);
  }, [invite, fetchCode]);

  const handleCopy = async () => {
    if (!invite) return;
    await navigator.clipboard.writeText(invite.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareLink = async () => {
    if (!invite) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Join my family", url: invite.payload });
      } catch {
        // share dismissed — nothing to do
      }
      return;
    }
    await navigator.clipboard.writeText(invite.payload);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const canShare = typeof navigator !== "undefined" && !!navigator.share;
  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;

  return (
    <div className="container" style={{ padding: "16px" }}>
      <div className="header-row">
        <h1 className="header-title">Invite</h1>
        <button onClick={onBack} className="btn-close">&times;</button>
      </div>

      {error ? (
        <p style={{ color: "#e53e3e" }}>{error}</p>
      ) : !invite ? (
        <Spinner />
      ) : (
        <>
          <p className="invite-subtitle">
            Scan this QR code on another device to join your family.
          </p>

          <div className="invite-qr">
            <QRCodeSVG value={invite.payload} size={200} />
          </div>

          <div className="invite-divider">
            <span>or share this code</span>
          </div>

          <div className="invite-code-box">
            <code className="invite-short-code">{invite.code}</code>
            <span className="invite-timer">
              Expires in {mins}:{secs.toString().padStart(2, "0")}
            </span>
            <button onClick={handleCopy} className="btn-primary btn-small">
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>

          <div className="invite-divider">
            <span>or share this link</span>
          </div>

          <div className="invite-link-box">
            <span className="invite-link-text">{invite.payload}</span>
            <button onClick={handleShareLink} className="btn-primary btn-small">
              {linkCopied ? "Copied!" : canShare ? "Share" : "Copy link"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
